import { Typography, Card, Divider, Box, Grid } from '@mui/material';
import { RootState } from '../../redux/store';
import { roundDoubleNumbers } from '../../helpers/currency';
import { GrayTypography, PriceGrayTypography, PriceTypography } from './shoppingCartStyles';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux/hooks';
import { useEffect, useState } from 'react';
import { ShoppingCartCouponType } from 'orderme-api-integration-client';

export interface CartInfoProps {
  isPurchase?: boolean;
  isSmallFont?: boolean;
  hideHeader?: boolean;
}

export function CartInfo({ isPurchase, isSmallFont, hideHeader }: CartInfoProps) {
  const isLoggedIn = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const usedCredits = useAppSelector((state: RootState) => state.shoppingCartState.usedCredits);
  const shoppingCartItems = useAppSelector((state: RootState) => state.shoppingCartState.items);
  const priceToPay = useAppSelector((state: RootState) => state.shoppingCartState.priceToPay);
  const totalPrice = useAppSelector((state: RootState) => state.shoppingCartState.totalPrice);
  const totalOriginalPrice = useAppSelector((state: RootState) => state.shoppingCartState.totalOriginalPrice);
  const totalDiscount = useAppSelector((state: RootState) => state.shoppingCartState.totalDiscount);
  const couponDiscount = useAppSelector((state: RootState) => state.shoppingCartState.couponDiscount);
  const couponType = useAppSelector((state: RootState) => state.shoppingCartState.coupon?.type);
  const loyaltyPrice = useAppSelector((state: RootState) => state.shoppingCartState.loyaltyPrice);
  const deliveryPrice = useAppSelector((state: RootState) => state.shoppingCartState.deliveryPrice);
  const packsPrice = useAppSelector((state: RootState) => state.shoppingCartState.packsPrice);
  const packs = useAppSelector((state: RootState) => state.shoppingCartState.packs);
  const hasDelivery = useAppSelector((state: RootState) => state.shoppingCartState.hasDelivery);
  const { t } = useTranslation(['order']);
  const [originalPrice, setOriginalPrice] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [finalPrice, setFinalPrice] = useState<number>(0);

  useEffect(() => {
    if (isPurchase) {
      setPrice(totalPrice + deliveryPrice + packsPrice - couponDiscount);
    } else {
      setPrice(totalPrice);
    }
  }, [totalPrice, isPurchase, deliveryPrice, packsPrice, couponDiscount]);

  useEffect(() => {
    if (isPurchase) {
      setOriginalPrice(totalOriginalPrice);
    } else {
      setOriginalPrice(totalOriginalPrice);
    }
  }, [totalOriginalPrice, isPurchase, packsPrice]);

  useEffect(() => {
    if (isPurchase) {
      setFinalPrice(priceToPay);
    } else {
      setFinalPrice(totalPrice);
    }
  }, [totalPrice, isPurchase, priceToPay]);

  return (
    <Box sx={{ marginBottom: '10px', width: '100%' }}>
      <Card
        sx={{
          padding: '20px',
          borderRadius: '10px',
          backgroundColor: '#f5f5f5',
        }}
      >
        {!hideHeader && (
          <Typography sx={{ fontWeight: 'bold', mb: '10px', fontSize: isSmallFont ? '16px' : '18px' }}>
            {t('payment:cartSummary')}
          </Typography>
        )}
        {shoppingCartItems?.length > 0 && (
          <>
            <Grid container>
              <Grid size="grow">
                <GrayTypography isSmallFont={isSmallFont}>{t('payment:originalCartWorth')} </GrayTypography>
              </Grid>
              <Grid size="auto">
                <PriceGrayTypography isSmallFont={isSmallFont}>
                  {roundDoubleNumbers(originalPrice)} €
                </PriceGrayTypography>
              </Grid>
            </Grid>
            {totalDiscount > 0 && (
              <Grid container>
                <Grid size="grow">
                  <Typography sx={{ fontSize: isSmallFont ? '14px' : '16px' }}>
                    {couponType === ShoppingCartCouponType.PercentageDiscountFromOriginalPrice
                      ? t('payment:couponDiscount')
                      : t('payment:loyaltyDiscount')}
                  </Typography>
                </Grid>
                <Grid size="auto">
                  <PriceTypography isSmallFont={isSmallFont}>
                    {'-' + roundDoubleNumbers(totalDiscount)} €
                  </PriceTypography>
                </Grid>
              </Grid>
            )}
            {isPurchase && couponDiscount > 0 && (
              <Grid container>
                <Grid size="grow">
                  <Typography sx={{ fontSize: isSmallFont ? '14px' : '16px' }}>
                    {t('payment:couponDiscount')}
                  </Typography>
                </Grid>
                <Grid size="auto">
                  <PriceTypography isSmallFont={isSmallFont}>-{roundDoubleNumbers(couponDiscount)} €</PriceTypography>
                </Grid>
              </Grid>
            )}
            {isPurchase && hasDelivery && (
              <Grid container>
                <Grid size="grow">
                  <GrayTypography isSmallFont={isSmallFont}>{t('payment:delivery')}</GrayTypography>
                </Grid>
                <Grid size="auto">
                  <PriceGrayTypography isSmallFont={isSmallFont} sx={{ textTransform: 'uppercase' }}>
                    {deliveryPrice > 0 ? `${roundDoubleNumbers(deliveryPrice)} €` : t('payment:freeDelivery')}
                  </PriceGrayTypography>
                </Grid>
              </Grid>
            )}
            {isPurchase && packs?.length > 0 && (
              <>
                {packs.map((pack, index) => (
                  <Grid container key={index}>
                    <Grid size="grow">
                      <GrayTypography isSmallFont={isSmallFont}>
                        {pack.name}{' '}
                        <span style={{ fontSize: isSmallFont ? '10px' : '14px' }}>
                          {t('order:pieces', { count: pack.count || 1 })}
                        </span>
                      </GrayTypography>
                    </Grid>
                    <Grid size="auto">
                      <PriceGrayTypography isSmallFont={isSmallFont}>
                        {roundDoubleNumbers(pack.count * pack.itemPrice || 0)} €
                      </PriceGrayTypography>
                    </Grid>
                  </Grid>
                ))}
              </>
            )}
            <Grid container>
              <Grid size="grow">
                <GrayTypography isSmallFont={isSmallFont}>{t('payment:cartWorth')} </GrayTypography>
              </Grid>
              <Grid size="auto">
                <PriceGrayTypography isSmallFont={isSmallFont}>{roundDoubleNumbers(price)} €</PriceGrayTypography>
              </Grid>
            </Grid>

            <Divider sx={{ margin: '5px' }} />

            {isPurchase && usedCredits > 0 && (
              <>
                <Grid container>
                  <Grid size="grow">
                    <Typography sx={{ fontSize: isSmallFont ? '14px' : '16px' }}>{t('payment:creditsUsed')}</Typography>
                  </Grid>
                  <Grid size="auto">
                    <PriceTypography isSmallFont={isSmallFont}>-{roundDoubleNumbers(usedCredits)} €</PriceTypography>
                  </Grid>
                </Grid>
                <Divider sx={{ margin: '5px' }} />
              </>
            )}
            {isLoggedIn && (
              <Grid container>
                <Grid size="grow">
                  <Typography sx={{ fontWeight: 'bold', fontSize: isSmallFont ? '14px' : '16px' }}>
                    {t('payment:finalPrice')}
                  </Typography>
                </Grid>
                <Grid size="auto">
                  <PriceTypography isSmallFont={isSmallFont} sx={{ fontWeight: 'bold' }}>
                    {roundDoubleNumbers(finalPrice)} €
                  </PriceTypography>
                </Grid>
              </Grid>
            )}
            {!isLoggedIn && (
              <Grid container>
                <Grid size="grow">
                  <Typography sx={{ fontWeight: 'bold', fontSize: isSmallFont ? '14px' : '16px' }}>
                    {t('payment:priceForFriends')}
                  </Typography>
                </Grid>
                <Grid size="auto">
                  <PriceTypography
                    isSmallFont={isSmallFont}
                    sx={{
                      backgroundColor: 'secondary.main',
                      borderRadius: '10px',
                      padding: '0px 8px',
                      color: 'white',
                    }}
                  >
                    {roundDoubleNumbers(loyaltyPrice + deliveryPrice)} €
                  </PriceTypography>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Card>
    </Box>
  );
}
